import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CalendarClock, ChevronRight, Clock, MapPin, Plus, Tag, User, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';

const MODULE_LABEL = { both: 'Tout le dossier', easy_power: 'Courtage', chantier: 'Travaux d’optimisation' };

function fmtDateTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) +
            ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso?.slice(0, 16) || ''; }
}

function EventWidgetItem({ evt, onClick }) {
    const isToday = evt.start_datetime && new Date(evt.start_datetime).toDateString() === new Date().toDateString();
    return (
        <div
            className={cn(
                "flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors",
                isToday && "border-blue-200 bg-blue-50/50"
            )}
            onClick={onClick}
            data-testid={`event-widget-item-${evt.id}`}
        >
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: evt.color || '#3b82f6' }} />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{evt.title}</p>
                <div className="flex items-center gap-2 text-xs">
                    {evt.prospect_name && <span className="text-blue-600 truncate"><Tag className="w-3 h-3 inline mr-0.5" />{evt.prospect_name}</span>}
                    <span className={cn(isToday ? "text-blue-600 font-medium" : "text-slate-500")}>
                        <Clock className="w-3 h-3 inline mr-0.5" />
                        {fmtDateTime(evt.start_datetime)}
                        {isToday && " · aujourd'hui"}
                    </span>
                </div>
            </div>
        </div>
    );
}

export function DashboardEventsWidget({ events = [] }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [selected, setSelected] = useState(null);

    // MES événements à venir (créateur / participant / assigné) — mêmes règles que les tâches
    const myUpcoming = useMemo(() => {
        const uid = user?.id;
        const now = new Date();
        return events
            .filter(e => !e.is_deleted)
            .filter(e => (e.participant_ids || []).includes(uid) || e.assigned_to === uid || e.created_by === uid)
            .filter(e => e.status !== 'cancelled')
            .filter(e => e.start_datetime && new Date(e.end_datetime || e.start_datetime) >= now)
            .sort((a, b) => (a.start_datetime || '9999') < (b.start_datetime || '9999') ? -1 : 1);
    }, [events, user]);

    const todayCount = useMemo(() =>
        myUpcoming.filter(e => new Date(e.start_datetime).toDateString() === new Date().toDateString()).length,
        [myUpcoming]);

    const displayEvents = myUpcoming.slice(0, 5);
    const openProspect = (pid) => { if (pid) { setSelected(null); navigate(`/prospects/${pid}`); } };

    return (
        <Card data-testid="events-widget">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <CalendarClock className="w-5 h-5" />
                        Mes événements
                        {todayCount > 0 && (
                            <span className="flex items-center gap-1 text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                {todayCount} aujourd'hui
                            </span>
                        )}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')} data-testid="events-widget-see-all">
                        Voir tout <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {displayEvents.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                        <CalendarClock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">Aucun événement à venir</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/calendar')}>
                            <Plus className="w-4 h-4 mr-1" />Ouvrir l'agenda
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {displayEvents.map(evt => (
                            <EventWidgetItem key={evt.id} evt={evt} onClick={() => setSelected(evt)} />
                        ))}
                        {myUpcoming.length > 5 && (
                            <Button variant="ghost" size="sm" className="w-full text-slate-500 hover:text-slate-700" onClick={() => navigate('/calendar')}>
                                + {myUpcoming.length - 5} autres événements
                            </Button>
                        )}
                    </div>
                )}
            </CardContent>

            {/* Détail d'un événement : identifier l'événement + ouvrir le prospect lié */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-md" data-testid="event-detail-dialog">
                    {selected && (
                        <>
                            <DialogHeader><DialogTitle className="pr-6">{selected.title}</DialogTitle></DialogHeader>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{fmtDateTime(selected.start_datetime)}</Badge>
                                    {selected.module && <Badge variant="outline">{MODULE_LABEL[selected.module] || selected.module}</Badge>}
                                </div>
                                {selected.location && (
                                    <p className="flex items-center gap-1 text-slate-600"><MapPin className="w-4 h-4" />{selected.location}</p>
                                )}
                                {selected.description && <p className="text-slate-600 whitespace-pre-wrap">{selected.description}</p>}
                                {selected.assigned_to_name && (
                                    <p className="flex items-center gap-1 text-slate-600"><User className="w-4 h-4" />{selected.assigned_to_name}</p>
                                )}
                                {selected.prospect_id ? (
                                    <Button className="w-full mt-2" onClick={() => openProspect(selected.prospect_id)} data-testid="event-detail-open-prospect">
                                        <ExternalLink className="w-4 h-4 mr-2" />Ouvrir le dossier {selected.prospect_name || ''}
                                    </Button>
                                ) : (
                                    <p className="text-xs text-slate-400">Aucun dossier lié</p>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
}
